const PROJECT_CONTENT_SCHEMA_VERSION = 1;
const DRAWING_SHEETS_SCHEMA_VERSION = 1;
const MAX_DRAWING_SHEETS = 100;
const MAX_CONTENT_SOURCES = 100;
const MAX_COLLECTIONS_PER_SOURCE = 100;
const MAX_CONTENT_PATH_DEPTH = 8;
const MAX_CONTENT_COUNT = 10_000_000;
const FORBIDDEN_PATH_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);

function contentSource(ownerId, collectionPaths) {
  return Object.freeze({
    ownerId,
    collectionPaths: Object.freeze(collectionPaths.map(path => Object.freeze([...path])))
  });
}

const CORE_PROJECT_CONTENT_SOURCES = Object.freeze([
  contentSource('core-obstacles', [['obstacles']]),
  contentSource('core_object_assets', [['objectAssets']]),
  contentSource('core-dimensions', [['dimensions']]),
  contentSource('core-area-measurements', [['areaMeasurements']]),
  contentSource('core-text-annotations', [['textAnnotations']]),
  contentSource('sketchWalls', [['sketchWalls', 'walls']])
]);

const rememberedManifestByProject = new WeakMap();

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeOwnerId(value) {
  const ownerId = String(value || '').trim();
  return ownerId && ownerId.length <= 120 ? ownerId : '';
}

function normalizeProjectContentPath(value) {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_CONTENT_PATH_DEPTH) return null;
  const path = [];
  for (const rawSegment of value) {
    const segment = String(rawSegment || '').trim();
    if (!segment || segment.length > 120 || FORBIDDEN_PATH_SEGMENTS.has(segment)) return null;
    path.push(segment);
  }
  return path;
}

function projectFromPersistenceState(state) {
  if (!isPlainObject(state)) return null;
  return isPlainObject(state.project) ? state.project : state;
}

function resolveProjectContentCollection(project, path) {
  const normalizedPath = normalizeProjectContentPath(path);
  if (!isPlainObject(project) || !normalizedPath) return null;
  let value = project;
  for (const segment of normalizedPath) {
    if (!isPlainObject(value) || !Object.prototype.hasOwnProperty.call(value, segment)) return null;
    value = value[segment];
  }
  return Array.isArray(value) ? value : null;
}

function normalizeCollectionDeclaration(value) {
  if (!isPlainObject(value)) return null;
  const path = normalizeProjectContentPath(value.path);
  const count = Number(value.count);
  if (!path || !Number.isSafeInteger(count) || count <= 0 || count > MAX_CONTENT_COUNT) return null;
  return { path, count };
}

function normalizeProjectContentManifest(value) {
  if (!isPlainObject(value) || Number(value.schemaVersion) !== PROJECT_CONTENT_SCHEMA_VERSION) return null;
  if (!Array.isArray(value.sources) || value.sources.length === 0 || value.sources.length > MAX_CONTENT_SOURCES) return null;
  const totalObjectCount = Number(value.totalObjectCount);
  if (!Number.isSafeInteger(totalObjectCount) || totalObjectCount <= 0 || totalObjectCount > MAX_CONTENT_COUNT) return null;

  const sources = [];
  const seenPaths = new Set();
  let declaredTotal = 0;
  for (const rawSource of value.sources) {
    if (!isPlainObject(rawSource)) return null;
    const ownerId = normalizeOwnerId(rawSource.ownerId);
    if (!ownerId || !Array.isArray(rawSource.collections) || rawSource.collections.length === 0
      || rawSource.collections.length > MAX_COLLECTIONS_PER_SOURCE) return null;
    const collections = [];
    for (const rawCollection of rawSource.collections) {
      const collection = normalizeCollectionDeclaration(rawCollection);
      if (!collection) return null;
      const pathKey = collection.path.join('\u0000');
      if (seenPaths.has(pathKey)) return null;
      seenPaths.add(pathKey);
      declaredTotal += collection.count;
      if (declaredTotal > MAX_CONTENT_COUNT) return null;
      collections.push(collection);
    }
    sources.push({ ownerId, collections });
  }
  if (declaredTotal !== totalObjectCount) return null;
  return { schemaVersion: PROJECT_CONTENT_SCHEMA_VERSION, sources, totalObjectCount };
}

function hasDeclaredProjectDrawingContent(projectOrState, manifest = null) {
  const project = projectFromPersistenceState(projectOrState);
  const sourceManifest = manifest || (isPlainObject(projectOrState) ? projectOrState.drawingContent : null);
  const normalizedManifest = normalizeProjectContentManifest(sourceManifest);
  if (!project || !normalizedManifest) return false;

  let actualTotal = 0;
  for (const source of normalizedManifest.sources) {
    for (const declaration of source.collections) {
      const collection = resolveProjectContentCollection(project, declaration.path);
      if (!collection || collection.length !== declaration.count) return false;
      actualTotal += collection.length;
    }
  }
  return actualTotal > 0 && actualTotal === normalizedManifest.totalObjectCount;
}

function createProjectContentRegistry(initialSources = []) {
  const sources = new Map();
  const ownerIdByPath = new Map();

  function registerSource(ownerIdValue, collectionPaths = []) {
    const ownerId = normalizeOwnerId(ownerIdValue);
    if (!ownerId || !Array.isArray(collectionPaths) || sources.has(ownerId)) return false;
    const paths = [];
    const seenPaths = new Set();
    for (const value of collectionPaths) {
      const path = normalizeProjectContentPath(value);
      if (!path) return false;
      const pathKey = path.join('\u0000');
      if (seenPaths.has(pathKey)) continue;
      if (ownerIdByPath.has(pathKey)) return false;
      // A container claim also owns its descendants. Only paths within this
      // pending source may overlap; all previously registered sources are foreign.
      for (const source of sources.values()) {
        if (source.collectionPaths.some(existingPath => (
          path.slice(0, Math.min(path.length, existingPath.length))
            .every((segment, index) => segment === existingPath[index])
        ))) return false;
      }
      seenPaths.add(pathKey);
      paths.push(Object.freeze(path));
    }
    if (paths.length === 0 || paths.length > MAX_COLLECTIONS_PER_SOURCE) return false;

    if (sources.size >= MAX_CONTENT_SOURCES) return false;

    sources.set(ownerId, Object.freeze({ ownerId, collectionPaths: Object.freeze(paths) }));
    for (const path of paths) ownerIdByPath.set(path.join('\u0000'), ownerId);
    return true;
  }

  function unregisterSource(ownerIdValue) {
    const ownerId = normalizeOwnerId(ownerIdValue);
    const source = sources.get(ownerId);
    if (!source) return false;
    sources.delete(ownerId);
    for (const path of source.collectionPaths) {
      const pathKey = path.join('\u0000');
      if (ownerIdByPath.get(pathKey) === ownerId) ownerIdByPath.delete(pathKey);
    }
    return true;
  }

  function definitions() {
    return Array.from(sources.values()).map(source => ({
      ownerId: source.ownerId,
      collectionPaths: source.collectionPaths.map(path => [...path])
    }));
  }

  function collectManifest(projectOrState) {
    const project = projectFromPersistenceState(projectOrState);
    const manifestSources = [];
    const seenPaths = new Set();
    let totalObjectCount = 0;
    if (project) {
      for (const source of sources.values()) {
        const collections = [];
        for (const path of source.collectionPaths) {
          const pathKey = path.join('\u0000');
          if (seenPaths.has(pathKey)) continue;
          const collection = resolveProjectContentCollection(project, path);
          if (!collection?.length) continue;
          seenPaths.add(pathKey);
          collections.push({ path: [...path], count: collection.length });
          totalObjectCount += collection.length;
        }
        if (collections.length) manifestSources.push({ ownerId: source.ownerId, collections });
      }
    }
    return {
      schemaVersion: PROJECT_CONTENT_SCHEMA_VERSION,
      sources: manifestSources,
      totalObjectCount
    };
  }

  function hasContent(projectOrState) {
    return collectManifest(projectOrState).totalObjectCount > 0;
  }

  for (const source of initialSources) {
    registerSource(source?.ownerId, source?.collectionPaths);
  }

  return Object.freeze({
    registerSource,
    unregisterSource,
    definitions,
    collectManifest,
    hasContent
  });
}

const defaultProjectContentRegistry = createProjectContentRegistry(CORE_PROJECT_CONTENT_SOURCES);

function manifestFromSources(project, sources = []) {
  if (!isPlainObject(project)) {
    return { schemaVersion: PROJECT_CONTENT_SCHEMA_VERSION, sources: [], totalObjectCount: 0 };
  }
  const manifestSources = [];
  let totalObjectCount = 0;
  for (const source of sources) {
    const ownerId = normalizeOwnerId(source?.ownerId);
    if (!ownerId || !Array.isArray(source?.collections)) continue;
    const collections = [];
    for (const declaration of source.collections) {
      const path = normalizeProjectContentPath(declaration?.path);
      const collection = resolveProjectContentCollection(project, path);
      if (!path || !collection?.length) continue;
      collections.push({ path, count: collection.length });
      totalObjectCount += collection.length;
    }
    if (collections.length) manifestSources.push({ ownerId, collections });
  }
  return { schemaVersion: PROJECT_CONTENT_SCHEMA_VERSION, sources: manifestSources, totalObjectCount };
}

function mergeProjectContentManifests(primary, secondary) {
  const sourcesByOwnerId = new Map();
  const seenPaths = new Set();
  let totalObjectCount = 0;

  for (const manifest of [primary, secondary]) {
    for (const source of manifest?.sources || []) {
      const ownerId = normalizeOwnerId(source?.ownerId);
      if (!ownerId) continue;
      let target = sourcesByOwnerId.get(ownerId);
      if (!target) {
        target = { ownerId, collections: [] };
        sourcesByOwnerId.set(ownerId, target);
      }
      for (const declaration of source.collections || []) {
        const path = normalizeProjectContentPath(declaration?.path);
        const count = Number(declaration?.count);
        if (!path || !Number.isSafeInteger(count) || count <= 0) continue;
        const pathKey = path.join('\u0000');
        if (seenPaths.has(pathKey)) continue;
        seenPaths.add(pathKey);
        target.collections.push({ path, count });
        totalObjectCount += count;
      }
    }
  }

  return {
    schemaVersion: PROJECT_CONTENT_SCHEMA_VERSION,
    sources: Array.from(sourcesByOwnerId.values()).filter(source => source.collections.length),
    totalObjectCount
  };
}

function collectProjectDrawingContentManifest(projectOrState) {
  const project = projectFromPersistenceState(projectOrState);
  const registeredManifest = defaultProjectContentRegistry.collectManifest(project);
  if (!project) return registeredManifest;

  const transportedManifest = projectOrState !== project && isPlainObject(projectOrState)
    ? projectOrState.drawingContent
    : null;
  const rememberedManifest = rememberedManifestByProject.get(project);
  if (rememberedManifest) {
    return mergeProjectContentManifests(
      registeredManifest,
      manifestFromSources(project, rememberedManifest.sources)
    );
  }

  const transportedDeclaration = normalizeProjectContentManifest(transportedManifest);
  if (!transportedDeclaration
    || !hasDeclaredProjectDrawingContent(project, transportedDeclaration)) {
    return registeredManifest;
  }
  return mergeProjectContentManifests(
    registeredManifest,
    manifestFromSources(project, transportedDeclaration.sources)
  );
}

function rememberProjectDrawingContentManifest(projectOrState, manifest = null) {
  const project = projectFromPersistenceState(projectOrState);
  if (!project) return false;
  const sourceManifest = manifest
    || (projectOrState !== project && isPlainObject(projectOrState) ? projectOrState.drawingContent : null);
  const normalized = normalizeProjectContentManifest(sourceManifest);
  if (!normalized || !hasDeclaredProjectDrawingContent(project, normalized)) {
    rememberedManifestByProject.delete(project);
    return false;
  }
  rememberedManifestByProject.set(project, normalized);
  return true;
}

function registerProjectContentSource(ownerId, collectionPaths) {
  return defaultProjectContentRegistry.registerSource(ownerId, collectionPaths);
}

function unregisterProjectContentSource(ownerId) {
  return defaultProjectContentRegistry.unregisterSource(ownerId);
}

function hasProjectDrawingContent(projectOrState) {
  if (collectProjectDrawingContentManifest(projectOrState).totalObjectCount > 0) return true;
  if (!isPlainObject(projectOrState) || !isPlainObject(projectOrState.drawingSheets)) return false;
  const envelope = projectOrState.drawingSheets;
  if (Number(envelope.schemaVersion) !== DRAWING_SHEETS_SCHEMA_VERSION
    || !Array.isArray(envelope.sheets)
    || envelope.sheets.length === 0
    || envelope.sheets.length > MAX_DRAWING_SHEETS) return false;
  const activeSheetId = String(envelope.activeSheetId || '').trim();
  const ids = new Set();
  let activeFound = false;
  const inactiveStates = [];
  for (const sheet of envelope.sheets) {
    if (!isPlainObject(sheet)) return false;
    const sheetId = String(sheet.id || '').trim();
    if (!sheetId || ids.has(sheetId)) return false;
    ids.add(sheetId);
    if (sheetId === activeSheetId) {
      activeFound = true;
      continue;
    }
    if (sheet.state !== null && !isPlainObject(sheet.state)) return false;
    if (isPlainObject(sheet.state)) inactiveStates.push(sheet.state);
  }
  return activeFound && inactiveStates.some(state => (
    collectProjectDrawingContentManifest(state).totalObjectCount > 0
  ));
}

export {
  PROJECT_CONTENT_SCHEMA_VERSION,
  CORE_PROJECT_CONTENT_SOURCES,
  collectProjectDrawingContentManifest,
  createProjectContentRegistry,
  hasDeclaredProjectDrawingContent,
  hasProjectDrawingContent,
  normalizeProjectContentManifest,
  normalizeProjectContentPath,
  projectFromPersistenceState,
  registerProjectContentSource,
  rememberProjectDrawingContentManifest,
  resolveProjectContentCollection,
  unregisterProjectContentSource
};
